import {useBoolean} from 'usehooks-ts'
import {CloseButton} from '@headlessui/react'
import {useTranslation} from 'react-i18next'

import {twMerge} from '@sdox/utils'
import {IconExternalLink} from '@sdox/icons'
import {IconExternalLinkLarge} from '@sdox/icons/large'

import {Button, LinkButton} from '../'
import {Media} from '../Media'
import {DialogNew} from '../DialogNew'

import type {MediaProps} from '../Media'
import type {ClassNameValue} from 'tailwind-merge'

export interface ExternalLinkProps {
  className?: ClassNameValue
  imageProps: MediaProps
  heading: string
  source?: string
  link?: string
  showBottomBorder?: boolean
  dialogContent?: string
}

export const ExternalLink = (props: ExternalLinkProps) => {
  const {className, imageProps, heading, source, link = '#', showBottomBorder, dialogContent} = props
  const {t} = useTranslation()
  const {setTrue: openDialog, setFalse: closeDialog, value: isDialogOpen} = useBoolean(false)
  const hasDialog = !!dialogContent
  // Show a dialog on <button> click if dialogContent is not empty,
  // otherwise use an <a> to directly link to the page.
  const WrapperComponent = hasDialog ? 'button' : 'a'
  const wrapperComponentProps = hasDialog
    ? {onClick: openDialog}
    : {href: link, target: '_blank', rel: 'noopener noreferrer'}

  return (
    <>
      <div className={'@container/block'}>
        <WrapperComponent
          {...wrapperComponentProps}
          className={twMerge(
            'flex flex-col items-center justify-between text-start @block-medium:grid @block-medium:grid-cols-[1fr_1.5fr]',
            'group relative cursor-pointer bg-surface-primary',
            'focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
            showBottomBorder && 'border-b-0 border-outline-secondary @block-medium:border-b-1 @block-medium:pb-block-medium',
            className,
          )}
        >
          <Media
            aspectRatio={'3/2'}
            scrim={true}
            // FIXME test pullout!
            className={twMerge('rounded-radius-md', imageProps.className)}
            {...imageProps}
          />
          <div
            className={'flex w-full items-start pr-spacing-16 pt-spacing-16 @block-medium:items-center @block-medium:gap-spacing-48 @block-medium:pb-spacing-16 @block-medium:pl-block-small @block-medium:pr-spacing-0'}
          >
            <div className={'flex grow flex-col gap-spacing-8'}>
              <h2 className={'line-clamp-3 text-p1-emphasized'}>{heading}</h2>
              <div className={'flex gap-spacing-2 text-glyph-default-secondary'}>
                {source ? <h3 className={'line-clamp-1 text-p1'}>{source}</h3> : null}
                <IconExternalLink className={'@block-medium:hidden'}/>
              </div>
            </div>
            <IconExternalLinkLarge className={'hidden text-glyph-default-secondary @block-medium:block'}/>
          </div>
        </WrapperComponent>
      </div>
      {hasDialog ? (
        <DialogNew
          heading={heading}
          open={isDialogOpen}
          onClose={closeDialog}
          size={'small'}
        >
          <DialogNew.Content>
            {source && (
              <DialogNew.Source className={'mb-spacing-12'}>
                {source}
              </DialogNew.Source>
            )}
            <span>
              {dialogContent}
            </span>
          </DialogNew.Content>
          <DialogNew.Footer className={'flex justify-end gap-spacing-12'}>
            <CloseButton
              as={Button}
              variant={'outline'}
              size={'medium'}
            >
              {t('common:cancel')}
            </CloseButton>
            <LinkButton
              variant={'filled-brand'}
              size={'medium'}
              href={link}
              target={'_blank'}
              rel={'noopener noreferrer'}
              onClick={closeDialog}
            >
              Link öffnen
            </LinkButton>
          </DialogNew.Footer>
        </DialogNew>
      ) : null}
    </>
  )
}
